'use client';

import cn from 'clsx';
import { format } from 'date-fns';
import { DayPicker } from 'react-day-picker';

import { IconButton } from '../../button';

import { calendarClassNames, calendarFormatters, DAYS } from './constants/calendar';
import { useCalendarDates } from './hooks/useCalendarDates';
import { CalendarProps } from './type/BaseCalendarType';
import { isSingleMode } from './utils/isSingleMode';

export function DateCalendar(props: CalendarProps) {
  const { className, baseDate, disabled, disableTargetDate } = props;

  const {
    renderedDate,
    isDisabledBoundary,
    isDisabledRange,
    handlePrevYear,
    handleNextYear,
    handlePrevMonth,
    handleNextMonth,
    isNextYearButtonDisabled,
    isNextMonthButtonDisabled,
    isPrevYearButtonDisabled,
    isPrevMonthButtonDisabled
  } = useCalendarDates({
    baseDate,
    disabled
  });

  const getDisabledDay = (date: Date) => {
    if (!disabled) {
      return false;
    } else if (isDisabledRange(disabled)) {
      return date >= disabled.from && date <= disabled.to;
    } else if (isDisabledBoundary(disabled)) {
      return date < disabled.before || date > disabled.after;
    }
    return false;
  };

  const isDisabledTargetDate = (date: Date) => {
    return disableTargetDate?.some((disableDate) => format(disableDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) ?? false;
  };

  const CaptionLabel = () => (
    <div className="w-full mb-tiny-large flex items-center gap-tiny-large p-tiny-large">
      <IconButton disabled={isPrevYearButtonDisabled} iconType="chevron-left-double" size={'xsmall'} onClick={handlePrevYear} />
      <IconButton disabled={isPrevMonthButtonDisabled} iconType="chevron-left" size={'xsmall'} onClick={handlePrevMonth} />
      <p className="body-large-semibold flex-1 text-center text-neutral-foreground-main">{`${renderedDate?.getFullYear()}년 ${renderedDate.getMonth() + 1}월`}</p>
      <IconButton disabled={isNextMonthButtonDisabled} iconType="chevron-right" size={'xsmall'} onClick={handleNextMonth} />
      <IconButton disabled={isNextYearButtonDisabled} iconType="chevron-right-double" size={'xsmall'} onClick={handleNextYear} />
    </div>
  );

  const handleDayClick = (date: Date) => {
    if (isDisabledTargetDate(date)) return;

    if (isSingleMode(props)) {
      props.onSelectDate(date);
    } else {
      const currentDates = props.selectedDate ?? [];
      const isAlreadySelected = currentDates.some((d) => d.getTime() === date.getTime());
      if (isAlreadySelected) {
        props.onSelectDate(currentDates.filter((d) => d.getTime() !== date.getTime()));
      } else {
        props.onSelectDate([...currentDates, date]);
      }
    }
  };

  const DayComponent = ({ day: { date, displayMonth }, modifiers }: any) => {
    const currentMonth = date.getMonth();
    const displayedMonth = displayMonth.getMonth();
    const isDisplayedDay = currentMonth === displayedMonth;
    const isSelected = modifiers.selected;
    const isDisabled = getDisabledDay(date);
    const isTargetDisabled = isDisabledTargetDate(date);

    if (!isDisplayedDay) {
      return <div className={'flex-1 flex justify-center items-center py-tiny-large'} />;
    }

    return (
      <div
        onClick={() => handleDayClick(date)}
        className={cn(
          'group flex-1 flex justify-center items-center py-tiny-large text-neutral-foreground-main body-medium-semibold',
          {
            'cursor-pointer': !isSelected
          },
          { 'pointer-events-none !text-neutral-foreground-disabled': isDisabled || isTargetDisabled },
          { '!text-bzc-red-600': date.getDay() === 0 }
        )}
      >
        {isSelected && <div className="absolute z-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary-background-main" />}
        {isTargetDisabled && <div className="absolute z-0 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-background-disabled-main" />}
        <div
          className={cn('absolute flex h-8 w-8 items-center justify-center rounded-full', {
            'group-hover:bg-neutral-background-hovered&pressed': !isSelected && !isDisabled && !isTargetDisabled
          })}
        />
        <span className={cn('z-10', { 'text-neutral-foreground-inverse-main': isSelected })}>{date.getDate()}</span>
      </div>
    );
  };

  const commonProps = {
    month: renderedDate,
    showOutsideDays: false,
    navLayout: 'around' as const,
    hideNavigation: true,
    components: {
      CaptionLabel,
      Day: DayComponent
    },
    className: cn(className, 'pb-tiny-medium'),
    classNames: {
      ...calendarClassNames,
      weekdays: 'flex items-center justify-between',
      weekday: 'text-neutral-foreground-lowest caption-xsmall-regular p-tiny-medium flex-1'
    },
    formatters: {
      formatWeekdayName: (date: Date) => DAYS[date.getDay()] as string,
      ...calendarFormatters
    }
  };

  if (isSingleMode(props)) {
    return (
      <DayPicker
        mode="single"
        selected={props.selectedDate}
        onSelect={(date) => {
          if (date) props.onSelectDate(date);
        }}
        {...commonProps}
      />
    );
  }

  return (
    <DayPicker
      mode="multiple"
      selected={props.selectedDate}
      onSelect={(date) => {
        if (date) props.onSelectDate(date);
      }}
      {...commonProps}
    />
  );
}
